import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface Deal {
  id: string;
  deal_title: string;
  property_address: string;
  current_balance: number;
  payment_amount: number;
  interest_rate: number;
  payments_remaining: number;
  balloon_amount: number | null;
}

export function DealPricingForm() {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [selectedDealId, setSelectedDealId] = useState<string>("");
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  
  // Pricing inputs
  const [offerPrice, setOfferPrice] = useState("");
  const [includeTaxes, setIncludeTaxes] = useState(false);
  const [includeInsurance, setIncludeInsurance] = useState(false);
  const [includeClosingCosts, setIncludeClosingCosts] = useState(false);
  const [taxAmount, setTaxAmount] = useState("");
  const [insuranceAmount, setInsuranceAmount] = useState("");
  const [closingCosts, setClosingCosts] = useState("");

  useEffect(() => {
    fetchDeals();
  }, []);

  useEffect(() => {
    if (selectedDealId) {
      const deal = deals.find(d => d.id === selectedDealId);
      setSelectedDeal(deal || null);
    }
  }, [selectedDealId, deals]);

  const fetchDeals = async () => {
    const { data, error } = await supabase
      .from("deals")
      .select("id, deal_title, property_address, current_balance, payment_amount, interest_rate, payments_remaining, balloon_amount")
      .order("deal_title", { ascending: true });

    if (error) {
      toast.error("Failed to fetch deals");
      return;
    }

    setDeals(data || []);
  };

  const calculateNetOffer = () => {
    const base = parseFloat(offerPrice) || 0;
    const taxes = includeTaxes ? (parseFloat(taxAmount) || 0) : 0;
    const insurance = includeInsurance ? (parseFloat(insuranceAmount) || 0) : 0;
    const closing = includeClosingCosts ? (parseFloat(closingCosts) || 0) : 0;
    
    return base - taxes - insurance - closing;
  };

  const calculateYield = () => {
    if (!selectedDeal || !offerPrice) return 0;
    const purchasePrice = parseFloat(offerPrice);
    const annualIncome = selectedDeal.payment_amount * 12;
    return ((annualIncome / purchasePrice) * 100);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleCopyQuote = () => {
    const netOffer = calculateNetOffer();
    const quote = `Deal Quote\n\nProperty: ${selectedDeal?.property_address}\nOffer Price: ${formatCurrency(parseFloat(offerPrice))}\nNet to Seller: ${formatCurrency(netOffer)}\nEstimated Yield: ${calculateYield().toFixed(2)}%`;
    
    navigator.clipboard.writeText(quote);
    toast.success("Quote copied to clipboard");
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Select Deal</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label>Deal</Label>
              <Select value={selectedDealId} onValueChange={setSelectedDealId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a deal" />
                </SelectTrigger>
                <SelectContent>
                  {deals.map((deal) => (
                    <SelectItem key={deal.id} value={deal.id}>
                      {deal.deal_title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedDeal && (
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Current Balance</p>
                  <p className="text-lg font-semibold">{formatCurrency(selectedDeal.current_balance)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Monthly Payment</p>
                  <p className="text-lg font-semibold">{formatCurrency(selectedDeal.payment_amount)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Interest Rate</p>
                  <p className="text-lg font-semibold">{selectedDeal.interest_rate}%</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Payments Remaining</p>
                  <p className="text-lg font-semibold">{selectedDeal.payments_remaining}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {selectedDeal && (
        <Card>
          <CardHeader>
            <CardTitle>Pricing Calculator</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>Offer Price</Label>
              <Input
                type="number"
                value={offerPrice}
                onChange={(e) => setOfferPrice(e.target.value)}
                placeholder="Enter offer price"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="taxes"
                  checked={includeTaxes}
                  onCheckedChange={(checked) => setIncludeTaxes(checked as boolean)}
                />
                <Label htmlFor="taxes" className="cursor-pointer">Include Back Taxes</Label>
              </div>
              {includeTaxes && (
                <Input
                  type="number"
                  value={taxAmount}
                  onChange={(e) => setTaxAmount(e.target.value)}
                  placeholder="Tax amount"
                />
              )}

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="insurance"
                  checked={includeInsurance}
                  onCheckedChange={(checked) => setIncludeInsurance(checked as boolean)}
                />
                <Label htmlFor="insurance" className="cursor-pointer">Include Insurance</Label>
              </div>
              {includeInsurance && (
                <Input
                  type="number"
                  value={insuranceAmount}
                  onChange={(e) => setInsuranceAmount(e.target.value)}
                  placeholder="Insurance amount"
                />
              )}

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="closing"
                  checked={includeClosingCosts}
                  onCheckedChange={(checked) => setIncludeClosingCosts(checked as boolean)}
                />
                <Label htmlFor="closing" className="cursor-pointer">Include Closing Costs</Label>
              </div>
              {includeClosingCosts && (
                <Input
                  type="number"
                  value={closingCosts}
                  onChange={(e) => setClosingCosts(e.target.value)}
                  placeholder="Closing costs"
                />
              )}
            </div>

            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between text-lg">
                <span className="font-medium">Offer Price:</span>
                <span>{formatCurrency(parseFloat(offerPrice) || 0)}</span>
              </div>
              {(includeTaxes || includeInsurance || includeClosingCosts) && (
                <>
                  {includeTaxes && (
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>- Taxes:</span>
                      <span>{formatCurrency(parseFloat(taxAmount) || 0)}</span>
                    </div>
                  )}
                  {includeInsurance && (
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>- Insurance:</span>
                      <span>{formatCurrency(parseFloat(insuranceAmount) || 0)}</span>
                    </div>
                  )}
                  {includeClosingCosts && (
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>- Closing Costs:</span>
                      <span>{formatCurrency(parseFloat(closingCosts) || 0)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xl font-bold border-t pt-2">
                    <span>Net to Seller:</span>
                    <span className="text-primary">{formatCurrency(calculateNetOffer())}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between text-lg border-t pt-2">
                <span className="font-medium">Estimated Yield:</span>
                <span className="text-primary font-bold">{calculateYield().toFixed(2)}%</span>
              </div>
            </div>

            <Button onClick={handleCopyQuote} className="w-full" disabled={!offerPrice}>
              Copy Quote to Clipboard
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
